import { SquareIcon } from "Assets/icons";
import LineGraph from "components/Graph/LineGraph";
import { useField, useFormikContext } from "formik";
import { createRef, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useGetTreatmentSummaryGoalsQuery, useLazyGetTreatmentSummaryGoalsQuery } from "store/services/clients/report.service";
import SessionAttendence from "./SessionAttendence";
import moment from "moment";
import { formatNumber, getUTCDate } from "helpers/common";
import { DISCHARGE_SUMMARY_REPORT_QUESTIONS, GOAL_TRACK_PARAMETERS, GOAL_TRACK_PARAMETERS_COLORS, REPORT_IDS, TREATMENT_SUMMARY_REPORT_QUESTIONS } from "config/constants";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { getTreatmentSummaryChartData } from "helpers/assessment";
import { cloneDeep } from "lodash";

const TreatmentSummaryGoalsReport = function TreatmentSummaryGoalsReport({ clientType, caseloadId, renderQuestion: RenderQuestion, questionId, assessmentId }) {
  const [attendance, setAttendance] = useState(null);
  const [goalSummary, setGoalSummary] = useState(null);

  const { setFieldValue, values, errors, getFieldMeta } = useFormikContext();

  const fieldValue = getFieldMeta(`${questionId}`)?.value?.other_details;
  const selectedGoals = fieldValue?.selected_goals || [];

  const goalNotesQId =
    assessmentId == REPORT_IDS.TREATMENT_SUMMARY_REPORT
      ? TREATMENT_SUMMARY_REPORT_QUESTIONS.goal_notes
      : assessmentId == REPORT_IDS.DISCHARGE_SUMMARY_REPORT
        ? DISCHARGE_SUMMARY_REPORT_QUESTIONS.goal_notes
        : null;

  const [trigger, { isFetching, isLoading }] = useLazyGetTreatmentSummaryGoalsQuery();

  const uniqueGoals = useMemo(() => {
    if (!goalSummary) {
      return [];
    }

    return _.uniqBy(goalSummary, 'goal_id');
  }, [goalSummary]);

  const attandanceData = useMemo(() => {
    if (!attendance?.length) {
      return {};
    }

    const totalSessions = attendance.length;
    const counts = _.countBy(attendance, 'attendance');
    const absentCount = counts['absent'] || 0;
    const persentCount = counts['present'] || 0;

    return {
      absent: {
        count: absentCount,
        percentage: formatNumber((absentCount * 100) / totalSessions, 2),
      },
      persent: {
        count: persentCount,
        percentage: formatNumber((persentCount * 100) / totalSessions, 2),
      },
      totalSessions
    };
  }, [attendance]);

  useEffect(() => {
    setFieldValue(`${questionId}.other_details.attendance`, attandanceData);
  }, [attandanceData])

  // get treatment summary data for the selected period
  useEffect(() => {
    if (!caseloadId || fieldValue?.period?.length !== 2) {
      return;
    }

    async function getData() {
      try {
        let payload = {
          caseloadId,
          data: {
            start_date: fieldValue?.period[0],
            end_date: fieldValue?.period[1],
          },
          clientType
        }
        let response = await trigger(payload).unwrap();
        setAttendance(response?.attendence);
        setGoalSummary(response?.assesments_goals);
      } catch (err) {
        console.error("Error getting treatment summary data", err);
      }
    }

    getData()
  }, [caseloadId, fieldValue?.period]);

  // save selected goal in formik field value
  const handleSelectGoal = useCallback(
    (id) => {
      let prevSelectedGoals = cloneDeep(selectedGoals);
      let newSelectedGoals = prevSelectedGoals?.includes(id)
        ? prevSelectedGoals.filter((goalId) => goalId !== id) // Remove if already selected
        : [...prevSelectedGoals, id]; // Add if not selected

      setFieldValue(`${questionId}.other_details.selected_goals`, newSelectedGoals);
    },
    [selectedGoals, setFieldValue, questionId]
  );

  const handleChangeChartData = useCallback(
    (data) => {
      setFieldValue(`${questionId}.other_details.chart_data`, {
        ...((fieldValue?.chart_data) || {}),
        [data.goal_id]: data,
      });
    },
    [fieldValue, setFieldValue, questionId]
  );

  return (
    <>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div>
          {isFetching && (
            <LoadingOverlay />
          )}
          <div className="py-3">
            <h6 className="m-0 fw-sbold themeBlue pb-1">
              Attendance
            </h6>
            <div className="border rounded-4">
              <SessionAttendence
                data={attandanceData}
              />
            </div>
          </div>
          {goalSummary && (
            <div className="py-3">
              <div
                className="p-3 px-lg-5"
                style={{ background: "#F6F6F6" }}
              >
                <h6 className="m-0 fw-sbold themeBlue pb-1">
                  Select Data to Graph
                </h6>
                <div className="table-responsive">
                  <table className="table border rounded">
                    <thead>
                      <tr>
                        {uniqueGoals?.map((_, index) => (
                          <th key={index} className="px-3 py-3">
                            <h6
                              className="m-0 d-flex align-items-start themeBlue fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              <span
                                className="icn me-1"
                                style={{ color: " rgb(61, 160, 170)" }}
                              >
                                ■{" "}
                              </span>{" "}
                              Goal
                            </h6>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {uniqueGoals?.map((goal, index) => (
                          <td key={index} className="px-3 py-3">
                            <div className="d-flex align-items-center gap-10">
                              <input
                                type="checkbox"
                                style={{ height: 20, width: 20 }}
                                className="form-check"
                                onChange={() => {
                                  handleSelectGoal(goal?.goal_id)
                                }}
                                checked={selectedGoals?.includes(goal?.goal_id)}
                              />
                              <p className="m-0">{goal?.assesmentGoalDetail?.title}</p>
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <div className="py-3">
            {selectedGoals?.map((goalId, index) => {
              let goal = uniqueGoals.find(goal => goal.goal_id === goalId);
              if (!goal) {
                return null;
              }
              return (
                <div key={index} className="py-3">
                  <GoalTrackingChart
                    index={index}
                    goal={goal}
                    goalSummary={goalSummary}
                    saveChartData={handleChangeChartData}
                  />
                  <div className="py-2">
                    <div className="">
                      <RenderQuestion
                        questionId={goalNotesQId}
                        fieldName={`${goalNotesQId}.other_details.goal_notes.${goalId}`}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
};

const GoalTrackingChart = memo(function GoalTrackingChart({ index, goal, goalSummary, saveChartData }) {
  const [enabledTrackParams, setEnabledTrackParams] = useState(() => {
    const obj = {};
    Object.keys(GOAL_TRACK_PARAMETERS).forEach(param => obj[param] = true)
    return obj;
  });

  const { dates, series } = useMemo(() => {
    if (!goal || !goalSummary?.length) {
      return {}
    }

    const chartData = getTreatmentSummaryChartData(goal, goalSummary, enabledTrackParams);

    // save chartData to chart ref for later saving with API
    let goalChartData = {
      goal_id: goal.goal_id,
      chartData,
      goalData: {
        goal_id: goal.goal_id,
        assesmentGoalDetail: goal?.assesmentGoalDetail
      }
    }

    saveChartData(goalChartData);

    return chartData;

  }, [goal, goalSummary, enabledTrackParams]);

  const handleToggleTrackParam = (param) => {
    setEnabledTrackParams((prevState) => ({
      ...prevState,
      [param]: !prevState[param]
    }))
  }

  return (
    <div className="py-2">
      <div
        className="cardCstm rounded-3 p-3"
        style={{ background: "#FBFBE6" }}
      >
        <div className="top d-flex align-items-start">
          <h5 className="m-0 fw-bold themeBlue">{index + 1}.</h5>
          <div className="px-3">
            <h5 className="m-0 pb-1 fw-sbold themeBlue border-bottom">
              Area
            </h5>
            <p className="m-0 themeBlue pt-1">
              {goal?.assesmentGoalDisciplineArea?.name}
            </p>
          </div>
          <div className="px-3">
            <h5 className="m-0 pb-1 fw-sbold themeBlue border-bottom">
              Goal
            </h5>
            <p className="m-0 themeBlue pt-1">{goal?.assesmentGoalDetail?.title}</p>
          </div>
        </div>
        <div className="bottom pt-4">
          <Row>
            <Col lg="8">
              <LineGraph
                dates={dates}
                series={series}
              />
            </Col>
            <Col lg="4">
              <div
                className="p-3 rounded-3 graphColorLevel"
                style={{ background: "#EBEBD0" }}
              >
                <ul
                  className="list-unstyled ps-0 mb-0 position-relative list"
                  style={{ zIndex: 9 }}
                >
                  {Object.entries(GOAL_TRACK_PARAMETERS).map((([param, label]) => {
                    let paramColor = GOAL_TRACK_PARAMETERS_COLORS[param];
                    let enabled = enabledTrackParams[param];
                    return (
                      <li className="py-2 d-flex align-items-center justify-content-between gap-10">
                        <div className="left d-flex align-items-center gap-10">
                          <div className="d-flex align-items-center gap-10">
                            <input
                              type="checkbox"
                              style={{ height: 20, width: 20 }}
                              className="form-check"
                              onChange={() => {
                                handleToggleTrackParam(param)
                              }}
                              checked={enabled}
                            />
                          </div>
                          <div className="d-flex align-items-center gap-10">
                            <p
                              className="d-flex m-0 bg-white align-items-center justify-content-center rounded-pill fw-bold"
                              style={{
                                border: `2px solid ${paramColor}`,
                                color: paramColor,
                                height: 30,
                                width: 30,
                              }}
                            >
                              {param}
                            </p>
                            <p className="m-0">{label}</p>
                          </div>
                        </div>
                        <div className="right">
                          <div
                            className="rounded"
                            style={{
                              background: paramColor,
                              height: 4,
                              width: 20,
                            }}
                          ></div>
                        </div>
                      </li>
                    )
                  }))}
                </ul>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  )
})

export default TreatmentSummaryGoalsReport;